import React, { useCallback, useEffect, useRef, useState } from "react";
import { API_BASE } from "../api/config";
import "./DropboxOrdnerWahl.css";

/**
 * Dropbox-Ordnerbrowser mit Brotkrumen (Welle 100).
 *
 * Props:
 *   pfad          aktueller Ordner (gesteuert)
 *   onPfad(p)     Ordner gewechselt
 *   mitDateien    PDFs des Ordners mit Häkchen zeigen
 *   auswahl       Set der angehakten Dateinamen (nur mitDateien)
 *   onAuswahl(s)  neue Auswahl; beim Ordnerwechsel werden alle PDFs vorausgewählt
 *   onPdfs(list)  Liste der PDFs im aktuellen Ordner (Namen); null = wird gerade geladen
 *   neuerOrdner   Knopf „Neuer Ordner" anbieten
 *   wurzel        oberster Ordner der Brotkrumen
 */
const WURZEL = "/Holthey Immobilien";

async function holeJson(url, opts) {
  const r = await fetch(url, opts);
  if (!r.ok) {
    let detail = "";
    try { detail = (await r.json()).detail || ""; } catch { /* keine JSON-Antwort */ }
    throw new Error(detail || `HTTP ${r.status}`);
  }
  return r.json();
}

export default function DropboxOrdnerWahl({
  pfad, onPfad, mitDateien = false, auswahl, onAuswahl, onPdfs, neuerOrdner = false, wurzel = WURZEL,
}) {
  const [eintraege, setEintraege] = useState([]);
  const [laden, setLaden] = useState(false);
  const [fehler, setFehler] = useState("");
  const [neuName, setNeuName] = useState("");
  const [zeigeNeu, setZeigeNeu] = useState(false);
  const anfrage = useRef(0);   // nur die letzte Antwort zählt (schnelles Klicken)

  const lade = useCallback(async (p) => {
    const nr = ++anfrage.current;
    setLaden(true); setFehler("");
    if (onPdfs) onPdfs(null);
    try {
      const d = await holeJson(`${API_BASE}/zuordnen/api/verschieben/browse-ordner?path=${encodeURIComponent(p)}`);
      if (nr !== anfrage.current) return;
      const liste = d.ordner || [];
      setEintraege(liste);
      const pdfs = liste.filter((e) => !e.is_folder && /\.pdf$/i.test(e.name)).map((e) => e.name);
      if (onPdfs) onPdfs(pdfs);
      if (mitDateien && onAuswahl) onAuswahl(new Set(pdfs));
    } catch (e) {
      if (nr !== anfrage.current) return;
      setFehler(String(e.message || e));
      setEintraege([]);
      if (onPdfs) onPdfs([]);
    } finally {
      if (nr === anfrage.current) setLaden(false);
    }
  // onAuswahl/onPdfs sind Setter der Elternkomponente — bewusst nicht als Abhängigkeit
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mitDateien]);

  useEffect(() => { if (pfad) lade(pfad); }, [pfad, lade]);

  const ordner = eintraege.filter((e) => e.is_folder);
  const pdfs = eintraege.filter((e) => !e.is_folder && /\.pdf$/i.test(e.name));

  // Brotkrumen ab der Wurzel
  const teile = [];
  const rel = pfad && pfad.toLowerCase().startsWith(wurzel.toLowerCase()) ? pfad.slice(wurzel.length) : "";
  let lauf = wurzel;
  teile.push({ name: wurzel.replace(/^\//, ""), pfad: wurzel });
  rel.split("/").filter(Boolean).forEach((t) => { lauf = `${lauf}/${t}`; teile.push({ name: t, pfad: lauf }); });

  const nachOben = () => {
    if (!pfad || pfad.toLowerCase() === wurzel.toLowerCase()) return;
    onPfad(pfad.substring(0, pfad.lastIndexOf("/")) || wurzel);
  };

  const toggle = (name) => {
    const s = new Set(auswahl || []);
    if (s.has(name)) s.delete(name); else s.add(name);
    onAuswahl(s);
  };

  const ordnerAnlegen = async () => {
    const n = neuName.trim().replace(/[\\/]/g, "-");
    if (!n) return;
    try {
      const d = await holeJson(`${API_BASE}/zuordnen/api/verschieben/ordner-erstellen`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pfad: `${pfad}/${n}` }),
      });
      setNeuName(""); setZeigeNeu(false);
      onPfad(d.path || `${pfad}/${n}`);
    } catch (e) { setFehler(String(e.message || e)); }
  };

  return (
    <div className="dow">
      <div className="dow-kopf">
        <button type="button" className="dow-hoch" onClick={nachOben} title="Eine Ebene höher"
          disabled={!pfad || pfad.toLowerCase() === wurzel.toLowerCase()}>↑</button>
        <div className="dow-krumen">
          {teile.map((t, i) => (
            <React.Fragment key={t.pfad}>
              {i > 0 && <span className="dow-trenner">›</span>}
              <button type="button" className={`dow-krume${i === teile.length - 1 ? " aktiv" : ""}`}
                onClick={() => onPfad(t.pfad)}>{t.name}</button>
            </React.Fragment>
          ))}
        </div>
        {neuerOrdner && (
          <button type="button" className="dow-neu" onClick={() => setZeigeNeu((v) => !v)}>+ Neuer Ordner</button>
        )}
      </div>

      {zeigeNeu && (
        <div className="dow-neu-zeile">
          <input value={neuName} onChange={(e) => setNeuName(e.target.value)} placeholder="Name des neuen Ordners"
            onKeyDown={(e) => { if (e.key === "Enter") ordnerAnlegen(); }} autoFocus />
          <button type="button" className="dow-neu-ok" onClick={ordnerAnlegen} disabled={!neuName.trim()}>Anlegen</button>
        </div>
      )}

      {fehler && <div className="dow-fehler">{fehler}</div>}

      <div className="dow-liste">
        {laden && <div className="dow-leer">Lade Ordner …</div>}
        {!laden && ordner.map((o) => (
          <button type="button" key={o.path} className="dow-ordner" onClick={() => onPfad(o.path)}>
            <span className="dow-icon">📁</span><span className="dow-name">{o.name}</span><span className="dow-pfeil">›</span>
          </button>
        ))}
        {!laden && mitDateien && pdfs.map((f) => (
          <label key={f.path} className={`dow-datei${auswahl && auswahl.has(f.name) ? " an" : ""}`}>
            <input type="checkbox" checked={!!(auswahl && auswahl.has(f.name))} onChange={() => toggle(f.name)} />
            <span className="dow-icon">📄</span><span className="dow-name">{f.name}</span>
          </label>
        ))}
        {!laden && !fehler && !ordner.length && (!mitDateien || !pdfs.length) && (
          <div className="dow-leer">{mitDateien ? "Keine Unterordner und keine PDFs in diesem Ordner." : "Keine Unterordner."}</div>
        )}
      </div>
    </div>
  );
}
